import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MessageSquare, FileSignature, Wallet, BellRing, Handshake, Heart, Unlock, Clock, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";
import { PageHead } from "@/pages/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const ICONS = { match: Sparkles, message: MessageSquare, signature: FileSignature, payment: Wallet, renewal: BellRing, request: Handshake, wishlist: Heart, unlock: Unlock, deadline: Clock, signup: Building2 };
const HOT = ["match", "wishlist", "unlock", "deadline", "signup"];

function EmailToggle() {
  const { user, setUser } = useAuth();
  const on = user?.email_alerts !== false;
  const flip = async () => {
    try {
      const { data } = await api.patch("/auth/me/preferences", { email_alerts: !on });
      setUser({ ...user, email_alerts: data.email_alerts });
      toast.success(data.email_alerts ? "Email alerts on" : "Email alerts off");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button onClick={flip} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors ${on ? "bg-white border border-[#E0218A] text-[#2A1240]" : "border border-[#2A1240]/15 text-slate-300"}`} data-testid="email-alerts-toggle">
      <Mail className="h-3.5 w-3.5" />Email alerts {on ? "on" : "off"}
    </button>
  );
}

export default function Alerts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/alerts").then((r) => {
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      if (r.data?.unread) api.post("/alerts/read-all").catch(() => {});
    }).catch(() => {});
  }, []);
  return (
    <div data-testid="alerts-page">
      <PageHead kicker="Smart alerts" title="What’s new for you"><EmailToggle /></PageHead>
      {!items.length && <p className="rounded-2xl border border-dashed border-[#2A1240]/15 p-10 text-center text-slate-300" data-testid="alerts-empty">You’re all caught up. Alerts for matches, messages, signatures, payments and renewals appear here.</p>}
      <div className="space-y-3">
        {items.map((a) => {
          const Icon = ICONS[a.kind] || BellRing;
          return (
            <Link key={a.id} to={a.link || "/app"} className={`flex items-start gap-4 rounded-2xl border p-5 transition-colors hover:border-[#E0218A]/40 ${a.read ? "border-[#2A1240]/10 bg-[#FFFFFF]" : "border-[#E0218A]/40 bg-[#E0218A]/5"}`} data-testid={`alert-${a.id}`}>
              <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${HOT.includes(a.kind) ? "bg-[#E0218A] text-white" : "border border-[#2A1240]/15 text-[#8E2BC4]"}`}><Icon className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-[#2A1240]">{a.title}</p>
                {a.body && <p className="text-sm text-slate-300 mt-0.5">{a.body}</p>}
                <p className="text-xs text-slate-400 mt-1">{fmtDateTime(a.created_at)}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
